/**
 * RECEIVE page. The code is typed (or pasted/scanned), it connects over
 * WebRTC and the files arrive directly with progress; when done they are offered
 * as a download. Cross-OS: the names are sanitized in `@enviar/core`.
 *
 * Code entries, all equivalent:
 * - type/paste `12345` or `ENVR1-12345`;
 * - open the QR deep link (`/receive?code=ENVR1-12345`) → connects on its own;
 * - scan the QR with the in-app camera (local jsQR, no frame leaves).
 *
 * RESUMPTION: if the connection is cut midway, the bytes already received are
 * kept (in-memory sinks). The sender reopens a new code and on typing it
 * here the transfer continues from where it was — the protocol negotiates the
 * offset with `accept.resume` and does not resend what is already there.
 */

import { createRoute, Link } from "@tanstack/solid-router";
import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { Download, FileDown, ScanLine } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import {
  isCompleteRoomCode,
  memorySink,
  parseTransferCode,
  receiveFiles,
  type TransferProgress,
} from "@enviar/core";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { PageBackground } from "@/components/ui/page-background";
import { TopBar } from "@/components/ui/top-bar";
import { canScanQr, QrScanner } from "@/components/transfer/qr-scanner";
import { joinTransferSession, type TransferSession } from "@/features/transfer/webrtc";
import { addHistoryEntry } from "@/features/transfer/history";
import { isNativePlatform, saveBlobUrl } from "@/lib/fs";

type Phase = "enter" | "connecting" | "receiving" | "done" | "error";

interface DownloadItem {
  name: string;
  url: string;
  size: number;
}

interface KeptSink {
  name: string;
  size: number;
  sink: ReturnType<typeof memorySink>;
}

function formatBytes(n: number): string {
  if (n < 1024) {
    return `${n} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Flat name for the `download` attribute (a path with `/` is not valid there). */
function downloadName(name: string): string {
  return name.replaceAll("/", "_");
}

function SanitizedReceivePage() {
  const { t } = useI18n();
  const [phase, setPhase] = createSignal<Phase>("enter");
  const [raw, setRaw] = createSignal("");
  const [progress, setProgress] = createSignal<TransferProgress>();
  const [downloads, setDownloads] = createSignal<DownloadItem[]>([]);
  const [receivedText, setReceivedText] = createSignal<string>();
  const [errorKind, setErrorKind] = createSignal<string>();
  const [scanning, setScanning] = createSignal(false);
  const [partialBytes, setPartialBytes] = createSignal(0);

  let session: TransferSession | undefined;
  // The sinks SURVIVE between sessions (resumption): if the same file
  // (name+size) is offered again after a cut, the sink is reused and
  // `accept.resume` negotiates the offset — what was already received is not resent.
  const sinks = new Map<number, KeptSink>();
  const delivered = new Set<number>();
  const objectUrls: string[] = [];
  onCleanup(() => {
    session?.cancel();
    for (const url of objectUrls) {
      URL.revokeObjectURL(url);
    }
  });

  const receivedSoFar = () =>
    [...sinks.values()].reduce((sum, kept) => sum + kept.sink.bytesReceived(), 0);

  const hasPartial = () =>
    sinks.size > 0 && [...sinks.values()].some((kept) => kept.sink.bytesReceived() < kept.size);

  const connect = () => {
    const code = parseTransferCode(raw());
    if (!code || !isCompleteRoomCode(code)) {
      setErrorKind("invalid-code");
      setPhase("error");
      return;
    }
    session?.cancel();
    setPhase("connecting");
    session = joinTransferSession(code, {
      onChannel: (channel) => {
        setPhase("receiving");
        receiveFiles(channel, {
          createSink: ({ index, name, size }) => {
            const kept = sinks.get(index);
            if (kept && kept.name === name && kept.size === size) {
              return kept.sink; // resume: same file → same sink
            }
            const sink = memorySink(size);
            sinks.set(index, { name, size, sink });
            return sink;
          },
          onProgress: setProgress,
          onText: (body) => setReceivedText(body),
          onFileDone: (file) => {
            if (delivered.has(file.index)) {
              return; // already delivered in a previous session (re-verified on resume)
            }
            const kept = sinks.get(file.index);
            if (!kept) {
              return;
            }
            delivered.add(file.index);
            const bytes = kept.sink.data();
            const ab = new ArrayBuffer(bytes.length);
            new Uint8Array(ab).set(bytes);
            const blob = new Blob([ab], { type: file.mime || "application/octet-stream" });
            const url = URL.createObjectURL(blob);
            objectUrls.push(url);
            setDownloads((prev) => [...prev, { name: file.name, url, size: file.size }]);
          },
          onDone: (files) => {
            setPhase("done");
            if (files.length > 0) {
              addHistoryEntry({
                direction: "received",
                fileNames: files.map((f) => f.name),
                totalBytes: files.reduce((sum, f) => sum + f.size, 0),
              });
            }
          },
          onError: (error) => {
            setErrorKind(error.message.startsWith("aborted:") ? "sender-aborted" : error.message);
            setPhase("error");
          },
        });
      },
      onClosed: () => {
        if (phase() !== "done") {
          setPartialBytes(receivedSoFar());
          setErrorKind("peer-gone");
          setPhase("error");
        }
      },
      onFailure: (kind) => {
        setErrorKind(kind);
        setPhase("error");
      },
    });
  };

  // QR deep link: /receive?code=ENVR1-12345 → autoconnects.
  onMount(() => {
    const param = new URLSearchParams(globalThis.location.search).get("code");
    if (param !== null && parseTransferCode(param) !== null) {
      setRaw(param);
      connect();
    }
  });

  const handleScan = (text: string): boolean => {
    const code = parseTransferCode(text);
    if (code === null) {
      return false; // foreign QR in front of camera: continue scanning
    }
    setRaw(code);
    connect();
    return true;
  };

  const percent = () => {
    const p = progress();
    if (!p || p.totalBytes === 0) {
      return 0;
    }
    return Math.round((p.totalSentBytes / p.totalBytes) * 100);
  };

  const codeEntry = (opts: { resume: boolean }) => (
    <div class="mx-auto flex w-full max-w-md flex-col gap-4 rounded-[var(--radius-2xl)] border border-border bg-surface/70 p-6 shadow-md backdrop-blur">
      <label class="flex flex-col gap-2">
        <span class="text-sm font-semibold text-foreground">
          {opts.resume ? t(msg`Enter the new code to resume`) : t(msg`Enter the code`)}
        </span>
        <input
          type="text"
          inputmode="numeric"
          data-testid="input-code"
          value={raw()}
          onInput={(e) => setRaw(e.currentTarget.value)}
          placeholder="12345"
          class="w-full rounded-xl border border-field-border bg-field-background p-4 text-center font-display text-4xl tracking-[0.25em] text-field-foreground tabular-nums outline-none transition-shadow placeholder:text-field-placeholder focus:ring-2 focus:ring-accent/40"
        />
      </label>
      <Button
        data-testid="btn-connect"
        onClick={connect}
        disabled={phase() === "connecting"}
        size="lg"
        class="text-base"
      >
        <Show
          when={phase() === "connecting"}
          fallback={opts.resume ? t(msg`Resume transfer`) : t(msg`Connect`)}
        >
          <span class="inline-flex items-center gap-2">
            <span class="relative flex h-2 w-2">
              <span class="animate-wave-ping absolute inline-flex h-full w-full rounded-full bg-accent-foreground/70" />
              <span class="relative inline-flex h-2 w-2 rounded-full bg-accent-foreground" />
            </span>
            {t(msg`Connecting…`)}
          </span>
        </Show>
      </Button>
      <Show when={!opts.resume && canScanQr()}>
        <Button
          variant="outline"
          data-testid="btn-scan-qr"
          onClick={() => setScanning(true)}
          class="gap-2"
        >
          <ScanLine class="h-4 w-4" />
          {t(msg`Scan QR`)}
        </Button>
      </Show>
    </div>
  );

  return (
    <main
      class="app-screen screen-enter mx-auto w-full max-w-xl gap-7"
      // When receiving several files, the screen widens on desktop so
      // that the download list forms a grid instead of a long column.
      classList={{ "lg:max-w-3xl": downloads().length > 1 }}
    >
      <PageBackground />
      <TopBar />

      <h1 class="font-display text-3xl tracking-tight">{t(msg`Receive`)}</h1>

      <Show when={phase() === "enter" || phase() === "connecting"}>
        {codeEntry({ resume: false })}
      </Show>

      <Show when={scanning()}>
        <QrScanner onResult={handleScan} onClose={() => setScanning(false)} />
      </Show>

      <Show when={phase() === "receiving"}>
        <div
          class="flex flex-col gap-2 rounded-[var(--radius-2xl)] border border-border bg-surface/70 p-6 shadow-md backdrop-blur"
          data-testid="transfer-progress"
        >
          <div class="flex items-center gap-2 text-sm text-muted-foreground">
            <FileDown class="h-4 w-4 text-accent" />
            {t(msg`Receiving…`)}
          </div>
          <div class="h-2.5 w-full overflow-hidden rounded-full bg-muted/25">
            <div
              class="h-full rounded-full bg-gradient-to-r from-accent to-brand-purple transition-[width] duration-300 ease-out"
              style={{ width: `${percent()}%` }}
            />
          </div>
          <p class="font-display text-lg text-foreground">{percent()}%</p>
        </div>
      </Show>

      <Show when={phase() === "done" || downloads().length > 0 || receivedText()}>
        <div
          class="flex flex-col gap-3 rounded-[var(--radius-2xl)] border border-border bg-surface/70 p-6 shadow-md backdrop-blur"
          data-testid="received"
        >
          <Show when={receivedText()}>
            <div class="flex flex-col gap-1.5">
              <p class="text-sm font-semibold text-foreground">{t(msg`Text received`)}</p>
              <p
                class="whitespace-pre-wrap break-words rounded-xl bg-field-background p-3 text-sm text-field-foreground"
                data-testid="received-text"
              >
                {receivedText()}
              </p>
            </div>
          </Show>
          <Show when={downloads().length > 0}>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <For each={downloads()}>
                {(item) => (
                  <a
                    href={item.url}
                    download={downloadName(item.name)}
                    data-testid="received-file"
                    // Web: the browser's native download (`<a download>`) serves the
                    // blob directly — without changes or permissions. Native (Tauri): the
                    // click is intercepted and it is saved with the OS "Save as…"
                    // dialog (better UX; the user chooses folder and name).
                    onClick={(e) => {
                      if (isNativePlatform()) {
                        e.preventDefault();
                        void saveBlobUrl(item.url, downloadName(item.name));
                      }
                    }}
                    class="press group flex items-center gap-3 rounded-xl border border-border bg-background/60 px-4 py-3 transition-colors hover:border-accent/50"
                  >
                    <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-success/15 text-success">
                      <Download class="h-5 w-5" />
                    </span>
                    <span class="flex min-w-0 flex-1 flex-col">
                      <span class="truncate text-sm font-medium text-foreground group-hover:text-accent">
                        {item.name}
                      </span>
                      <span class="text-xs text-muted-foreground">{formatBytes(item.size)}</span>
                    </span>
                  </a>
                )}
              </For>
            </div>
          </Show>
        </div>
      </Show>

      <Show when={phase() === "error"}>
        <div
          class="flex flex-col items-center gap-3 rounded-[var(--radius-2xl)] border border-destructive/30 bg-surface/70 p-6 text-center shadow-md backdrop-blur"
          data-testid="transfer-error"
        >
          <p class="text-sm font-medium text-destructive">{t(msg`Could not receive.`)}</p>
          <p class="text-xs text-muted-foreground" data-testid="error-kind">
            {errorKind()}
          </p>
          <Show
            when={hasPartial()}
            fallback={
              <Link
                to="/receive"
                class="text-sm font-medium text-accent underline-offset-4 hover:underline"
              >
                {t(msg`Try again`)}
              </Link>
            }
          >
            {/* Reanudación: lo recibido se conserva; con un código nuevo del
                emisor la transferencia sigue desde donde iba. */}
            <p class="text-sm text-foreground" data-testid="resume-offer">
              {t(msg`Kept so far:`)}{" "}
              <span data-testid="resume-from" class="font-semibold">
                {formatBytes(partialBytes())}
              </span>
            </p>
            {codeEntry({ resume: true })}
          </Show>
        </div>
      </Show>
    </main>
  );
}

export const receiveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/receive",
  component: SanitizedReceivePage,
});
